import Classnames from "classnames";
import React from "react";

import useHtmlProps from "../hooks/use-html-props";
import style from "./chip.styl";

type ChipProps = {
  /**
   * Set which sides should have no border radius.
   *
   * Used to 'attach' buttons to each other or other components.
   *
   * Array of values. Values are one of: `top`, `right`, `bottom`, `left`
   *
   * @default []
   */
  attach?: Array<"top" | "right" | "bottom" | "left">;
  bgColor?: string;
  borderColor?: string;
  iconColor?: string;
  textColor?: string;
  /**
   * @default 'rounded'
   */
  shape?: "rounded" | "circle" | "square";
  /**
   * @default 'medium'
   */
  size?: "small" | "medium" | "large";
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  "data-testid"?: string;
};

const Chip = React.forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
  const {
    attach = [],
    size = "medium",
    shape = "rounded",
    bgColor,
    textColor,
    borderColor,
    iconColor,
    children,
    className,
    style: styleProp,
    onClick,
    "data-testid": dataTestId,
  } = props;

  const htmlProps = useHtmlProps(props);

  const classNameMerged = Classnames(
    style.chip,
    style[size],
    style[shape],
    {
      [style.clickable]: !!onClick,
      [style.background]: !!bgColor,
    },
    ...attach.map((attachItem) => style[attachItem]),
    className,
  );

  const chipVariables: React.CSSProperties = {
    "--chip-bg-color": bgColor || "transparent",
    "--chip-text-color": textColor || "var(--neutral-600)",
    "--chip-border-color": borderColor || "transparent",
    "--chip-icon-color":
      iconColor ||
      (typeof bgColor !== "undefined" ? "var(--neutral)" : "var(--neutral)"),
    ...styleProp,
  } as React.CSSProperties;

  let childrenArray = React.Children.toArray(children);
  childrenArray = childrenArray.map((child, index) =>
    typeof child === "string" ? (
      <span key={`chip-child-${index}`}>{child}</span>
    ) : (
      child
    ),
  );

  return (
    <div
      ref={ref}
      {...htmlProps}
      className={classNameMerged}
      style={chipVariables}
      data-testid={dataTestId}
    >
      {childrenArray}
    </div>
  );
});

Chip.displayName = "Chip";

export default Chip;
